import React, { useState, createContext, useCallback, useRef, useMemo } from 'react';

const DEFAULT_DUNE_ID = '01c3be00-4ef0-406b-815e-80cd7cb480b1';
const DEFAULT_DUNE_AGENT = {
  label: 'Dune',
  value: DEFAULT_DUNE_ID,
  icon: 'https://feedalabs-agent-icon.s3.eu-north-1.amazonaws.com/DUNE-LOGO_fcac926a0b1a4771b64b2fbb00998c72.png',
  mode: 'agent',
};

const context = {};

export const TemporaryChatContext = createContext(context);

export const TemporaryChatContextProvider = props => {
  // System default is always Dune. Icon is hydrated from the agents API in PolyChatHeader.
  const [selectedAgent, setSelectedAgent] = useState(DEFAULT_DUNE_AGENT);

  const [chatMessages, setChatmessages] = useState([]);
  const [workflowChatMessages, setWorkflowChatmessages] = useState([]);
  const [toolImages, setToolImages] = useState([]);
  const [chatLoaderMessages, setChatLoaderMessages] = useState({
    newChunk: [{ tool_name: 'thinking...' }],
  });
  const [webSearch, setWebSearch] = useState(false);
  const [tempText, setTempText] = useState('');
  const [sessionID, setSessionID] = useState(null);
  const [sessionAgent, setSessionAgent] = useState(DEFAULT_DUNE_AGENT);
  const isActiveRef = useRef(true);

  const fakeAiLoader = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: true, isStreaming: true };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const fakeAiLoaderClear = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: false, isStreaming: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateUserChat = useCallback(message => {
    const userMessage = {
      human: message,
      ai: '',
      isStreaming: true,
      source: [],
      intermediate_steps: [
        {
          tool_name: 'thinking...',
          content: message,
          isLoading: true,
        },
      ],
    };
    setChatmessages(prev => [...prev, userMessage]);
  }, []);

  const updateBotChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false, isStreaming: true };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const finishStream = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isStreaming: false, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateDelegateBotChat = useCallback(delegateAgent => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length];
      const updatedLast = { ...last, delegate_agent: delegateAgent, ai: ' ', isLoading: false };
      return [...prev, updatedLast];
    });
  }, []);

  const updateStepsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const existingSteps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];

      const silencedSteps = existingSteps.map(step => ({
        ...step,
        isLoading: false,
      }));

      const updatedSteps = [...silencedSteps, { ...newChunk, isLoading: true }];

      const updatedLast = {
        ...last,
        intermediate_steps: updatedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateStepsResultsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const steps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];

      const stepExists = steps.some(step => step.tool_call_id === newChunk?.tool_call_id);

      const updatedSteps = stepExists
        ? steps.map(step => (step.tool_call_id === newChunk?.tool_call_id ? newChunk : step))
        : [...steps, newChunk];

      const updatedLast = {
        ...last,
        intermediate_steps: updatedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateAllStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];

      if (!last?.intermediate_steps || !Array.isArray(last?.intermediate_steps)) {
        return prev;
      }

      const finalizedSteps = last?.intermediate_steps?.map(step => ({
        ...step,
        isLoading: false,
      }));

      const updatedLast = {
        ...last,
        intermediate_steps: finalizedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, [setChatmessages]);

  const updateThinkingStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[0];

      const updatedLast = {
        ...last,
        isLoading: false,
      };

      return [updatedLast];
    });
  }, [setChatmessages]);

  const updateWorkflowUserChat = useCallback(message => {
    const userMessage = { human: message, ai: '', source: [] };
    setWorkflowChatmessages(prev => [...prev, userMessage]);
  }, []);

  const updateWorkflowBotChat = useCallback(newChunk => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateToolImages = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, images: newChunk?.search_results, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateChatLoaderMessage = useCallback(newChunk => {
    setChatLoaderMessages(prev => {
      const updatedLast = { newChunk, isLoading: false };
      return updatedLast;
    });
  }, []);

  // const updateChatLoaderMessage = useCallback(newChunkData => {
  //   setChatLoaderMessages(prev => {
  //     const currentChunks = prev?.newChunk || [];

  //     return {
  //       ...prev,
  //       newChunk: [...currentChunks, newChunkData],
  //       isLoading: false,
  //     };
  //   });
  // }, []);

  const updateChatSource = useCallback(newSources => {
    setChatmessages(prev => {
      const last = prev[prev.length - 1];

      const websearchSources = newSources.filter(src => src.type === 'google_search');
      const fileSources = newSources.filter(src => src.type === 'file');

      const updatedLast = {
        ...last,
        source: websearchSources.length > 0 ? websearchSources : [],
        files: fileSources.length > 0 ? fileSources : [],
      };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateWebSearch = useCallback(() => {
    setWebSearch(prev => !prev);
  }, []);

  const updateAgent = useCallback(agent => {
    localStorage.setItem('agent', JSON.stringify(agent));
    setSelectedAgent(agent);
  }, []);

  const toggleRef = useCallback(value => {
    isActiveRef.current = value;
  }, []);

  const contextValue = useMemo(
    () => ({
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      setSessionID,
      sessionID,
      setChatmessages,
      toolImages,
      setToolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      setTempText,
      tempText,
      selectedAgent,
      setSelectedAgent,
      updateAgent,
      setSessionAgent,
      sessionAgent,
      toggleRef,
      isActiveRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    }),
    [
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      sessionID,
      toolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      tempText,
      selectedAgent,
      updateAgent,
      sessionAgent,
      toggleRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    ],
  );

  return <TemporaryChatContext.Provider value={contextValue}>{props.children}</TemporaryChatContext.Provider>;
};
